import { useTheme } from '@emotion/react';
import { noop } from 'lodash';
import {
  createContext,
  type MutableRefObject,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';

import { useBoolState, useMemoizedBundle } from '@didaskill/ui/utils';

import { DEFAULT_AUDIO_VOLUME } from '../constants';
import type { Track, Tracklist } from '../types';
import {
  findTrackIndex,
  findTracklistIndex,
  nextTrackPosition,
  previousTrackPosition,
} from './utils';

export type AudioPlayerContextValue = {
  isAudioPlaying: boolean;
  isDrawerOpen: boolean;
  isMobileView: boolean;
  isPortrait: boolean;
  isMuted: boolean;
  isFileLoading: boolean;
  audioRef: MutableRefObject<HTMLAudioElement | null>;
  audioVolume: number;
  audioDuration: number;
  audioCurrentTime: number;
  onChangeAudioVolume: (newVolume: number) => void;
  onClickMute: () => void;
  onToggleAudioPlay: () => void;
  onOpenDrawer: () => void;
  onCloseDrawer: () => void;
  onClickPlayer: () => void;
  onPauseAudio: () => void;

  onNextTrack: () => void;
  onPreviousTrack: () => void;
  onNextTracklist: () => void;
  onPreviousTracklist: () => void;

  currentTracklist: Tracklist | null;
  currentTrack: Track | null;
  shouldAutoplay: boolean;
};

export const AudioPlayerContext = createContext<AudioPlayerContextValue>({
  isAudioPlaying: false,
  isDrawerOpen: false,
  isMobileView: false,
  isPortrait: false,
  isMuted: false,
  isFileLoading: false,
  audioRef: null,
  audioVolume: DEFAULT_AUDIO_VOLUME,
  audioDuration: -1,
  audioCurrentTime: -1,

  onChangeAudioVolume: noop,
  onClickMute: noop,
  onToggleAudioPlay: noop,
  onOpenDrawer: noop,
  onCloseDrawer: noop,
  onClickPlayer: noop,
  onPauseAudio: noop,

  onNextTrack: noop,
  onPreviousTrack: noop,
  onNextTracklist: noop,
  onPreviousTracklist: noop,

  currentTracklist: null,
  currentTrack: null,
  shouldAutoplay: false,
});

export type AudioPlayerContextProviderProps = {
  /** Callback called when the audio is toggled (play/pause). */
  onToggleAudioPlay?: () => void;
  /** Callback called when audio ends. */
  onAudioEnd?: () => void;
  /** Callback called when the playing track changes. */
  onChangeTrack?: (tracklistId: string, trackId: string) => void;
  /** List of tracklists. */
  tracklists: Tracklist[];
  /** Id of the initial tracklist. */
  initialTracklistId: string;
  /** Id of the initial track. */
  initialTrackId: string;
  /**
   * Increments when the user asks to play a block.
   * The first value does not start playback on its own.
   */
  playRequest?: number;
  /** Children to render. */
  children?: ReactNode;
};

export const AudioPlayerContextProviderBase = ({
  onToggleAudioPlay = noop,
  onAudioEnd = noop,
  onChangeTrack = noop,
  tracklists,
  initialTracklistId,
  initialTrackId,
  playRequest = 0,
  children = null,
}: AudioPlayerContextProviderProps) => {
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const seenPlayRequest = useRef(playRequest);

  const [currentRessourcesIndexes, setCurrentRessourcesIndexes] = useState({
    tracklist: findTracklistIndex(tracklists, initialTracklistId),
    track: findTrackIndex(tracklists, initialTracklistId, initialTrackId),
  });

  const isTracklistEmpty = !tracklists[currentRessourcesIndexes.tracklist]?.tracks.length;
  const currentTracklist = !isTracklistEmpty
    ? tracklists[currentRessourcesIndexes.tracklist]
    : null;
  const currentTrack = !isTracklistEmpty
    ? (tracklists[currentRessourcesIndexes.tracklist].tracks[currentRessourcesIndexes.track] ??
      null)
    : null;

  useEffect(() => {
    setCurrentRessourcesIndexes({
      tracklist: findTracklistIndex(tracklists, initialTracklistId),
      track: findTrackIndex(tracklists, initialTracklistId, initialTrackId),
    });
  }, [tracklists, initialTracklistId, initialTrackId]);

  // When the current track changes, we call the onChangeTrack callback.
  // There is infinite loop if we add onChangeTrack to the dependencies
  // biome-ignore lint/correctness/useExhaustiveDependencies: onChangeTrack would create an infinite loop
  useEffect(() => {
    if (
      tracklists[currentRessourcesIndexes.tracklist]?.id &&
      tracklists[currentRessourcesIndexes.tracklist].tracks[currentRessourcesIndexes.track]?.id
    ) {
      onChangeTrack(
        tracklists[currentRessourcesIndexes.tracklist].id,
        tracklists[currentRessourcesIndexes.tracklist].tracks[currentRessourcesIndexes.track].id,
      );
    }
  }, [currentRessourcesIndexes, tracklists]);

  const isFirstTracklist = currentRessourcesIndexes.tracklist === 0;
  const isLastTracklist =
    !isTracklistEmpty && currentRessourcesIndexes.tracklist === tracklists.length - 1;

  const onNextTrack = useCallback(() => {
    const next = nextTrackPosition(
      tracklists,
      currentRessourcesIndexes.tracklist,
      currentRessourcesIndexes.track,
    );

    if (!next) {
      setShouldAutoplay(false);
      return;
    }

    setShouldAutoplay(true);
    setCurrentRessourcesIndexes(next);
  }, [tracklists, currentRessourcesIndexes]);

  const onPreviousTrack = useCallback(() => {
    const previous = previousTrackPosition(
      tracklists,
      currentRessourcesIndexes.tracklist,
      currentRessourcesIndexes.track,
    );

    if (!previous) {
      return;
    }

    setShouldAutoplay(true);
    setCurrentRessourcesIndexes(previous);
  }, [tracklists, currentRessourcesIndexes]);

  const onNextTracklist = useCallback(() => {
    setShouldAutoplay(true);

    if (!isLastTracklist) {
      setCurrentRessourcesIndexes((prevIndex) => ({
        tracklist: prevIndex.tracklist + 1,
        track: 0,
      }));
    }
  }, [isLastTracklist]);

  const onPreviousTracklist = useCallback(() => {
    setShouldAutoplay(true);

    if (!isFirstTracklist) {
      setCurrentRessourcesIndexes((prevIndex) => ({
        tracklist: prevIndex.tracklist - 1,
        track: 0,
      }));
    }
  }, [isFirstTracklist]);

  const [isDrawerOpen, onOpenDrawer, onCloseDrawer] = useBoolState(false);
  const [isPortrait, setIsPortrait] = useState(true);
  const [isFileLoading, setIsFileLoading] = useState(false);

  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const [isMuted, onMute, onUnmute] = useBoolState(false);
  const [audioVolume, setAudioVolume] = useState(DEFAULT_AUDIO_VOLUME);

  const [audioDuration, setAudioDuration] = useState(-1);
  const [audioCurrentTime, setAudioCurrentTime] = useState(-1);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const shouldAutoplayRef = useRef(shouldAutoplay);
  const onNextTrackRef = useRef(onNextTrack);

  shouldAutoplayRef.current = shouldAutoplay;
  onNextTrackRef.current = onNextTrack;
  const theme = useTheme();

  const isMobileView = useMedia(`(max-width: ${theme.breakpoints.small})`);

  // TODO: metadata hack (https://developer.chrome.com/blog/media-session?hl=fr)

  /**
   * Starts playback of the current source.
   * A click can arrive before the browser has selected a source, or after a failed load.
   * `play()` then rejects with NotSupportedError; that rejection must not escape.
   * @returns Nothing. Playback starts once the element has a supported source.
   */
  const onPlayAudio = useCallback(async (): Promise<void> => {
    const audio = audioRef.current;

    if (!audio?.src) {
      return;
    }

    setShouldAutoplay(true);
    setIsFileLoading(true);

    try {
      // A failed load leaves the element with no supported source until it is reloaded.
      // Playback then resumes from the loadedmetadata listener.
      if (audio.error || audio.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
        audio.load();
        return;
      }

      await audio.play();
      setIsAudioPlaying(true);
      setIsFileLoading(false);
    } catch {
      setIsAudioPlaying(false);
      setIsFileLoading(false);
    }
  }, []);

  useEffect(() => {
    if (seenPlayRequest.current === playRequest) {
      return;
    }

    // The track index is applied in an earlier effect. Playing before that
    // commit restarts the previous source.
    if (!currentTrack?.id || currentTrack.id !== initialTrackId) {
      return;
    }

    seenPlayRequest.current = playRequest;
    shouldAutoplayRef.current = true;
    setShouldAutoplay(true);

    const audio = audioRef.current;
    const sourceIsCurrent =
      !!audio?.src &&
      !!currentTrack.href &&
      (audio.src === currentTrack.href || audio.currentSrc === currentTrack.href);

    // Elided blocks share one file. Restart only once the requested track is selected.
    if (
      sourceIsCurrent &&
      audio &&
      !audio.error &&
      audio.readyState >= HTMLMediaElement.HAVE_METADATA
    ) {
      audio.currentTime = 0;
      void onPlayAudio();
    }
  }, [playRequest, currentTrack?.id, currentTrack?.href, initialTrackId, onPlayAudio]);

  const onPauseAudio = useCallback(() => {
    if (!audioRef?.current) return;

    setShouldAutoplay(false);
    audioRef.current.pause();
    setIsAudioPlaying(false);
  }, []);

  const onToggleAudioPlayProxy = useCallback(async () => {
    if (!audioRef?.current) return;
    onToggleAudioPlay();

    if (audioRef.current.paused) {
      await onPlayAudio();
    } else {
      onPauseAudio();
    }
  }, [onPlayAudio, onPauseAudio, onToggleAudioPlay]);

  useEffect(() => {
    // We manage volume in a separate state so we can still change it while the src is loading
    const audio = audioRef.current;

    if (audio) {
      audio.volume = audioVolume;
    }
  }, [audioVolume]);

  useEffect(() => {
    const audio = audioRef.current;
    const href = currentTrack?.href;

    if (!audio || !href) return noop;

    const onLoadedMetadata = () => {
      setAudioDuration(audio.duration);

      if (!audio.currentTime) {
        setAudioCurrentTime(0);
      }

      if (shouldAutoplayRef.current && audio.paused) {
        void onPlayAudio();
      }
    };

    const onAudioEndProxy = () => {
      setIsAudioPlaying(false);
      onNextTrackRef.current();
    };

    const onTimeUpdate = () => {
      setAudioCurrentTime(audio.currentTime);
    };

    const onAudioError = () => {
      setIsFileLoading(false);
      setIsAudioPlaying(false);
    };

    const onOrientationChange = () => {
      const { innerHeight, innerWidth } = window;
      const isNewOrientationPortrait = innerWidth > innerHeight;

      setIsPortrait(isNewOrientationPortrait);
    };

    audio.addEventListener('ended', onAudioEndProxy);
    audio.addEventListener('error', onAudioError);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    window.addEventListener('orientationchange', onOrientationChange);

    // A cached file can fire loadedmetadata before this effect rebinds the listener.
    if (
      shouldAutoplayRef.current &&
      audio.paused &&
      audio.readyState >= HTMLMediaElement.HAVE_METADATA
    ) {
      void onPlayAudio();
    }

    return () => {
      audio.removeEventListener('ended', onAudioEndProxy);
      audio.removeEventListener('error', onAudioError);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      window.removeEventListener('orientationchange', onOrientationChange);
    };
  }, [onPlayAudio, currentTrack?.href]);

  const onClickPlayer = useCallback(() => {
    if (isMobileView) {
      onOpenDrawer();
    }
  }, [isMobileView, onOpenDrawer]);

  const onClickMute = useCallback(() => {
    if (isMuted) {
      onUnmute();
      if (!audioRef.current) return;
      audioRef.current.muted = false;
    } else {
      onMute();
      if (!audioRef.current) return;
      audioRef.current.muted = true;
    }
  }, [isMuted, onMute, onUnmute]);

  const onChangeAudioVolume = useCallback((newVolume: number) => {
    setAudioVolume(newVolume);
  }, []);

  const value = useMemoizedBundle<AudioPlayerContextValue>({
    isDrawerOpen,
    isAudioPlaying,
    isMobileView,
    isPortrait,
    isMuted,
    isFileLoading,

    audioRef,
    audioDuration,
    audioVolume,
    audioCurrentTime,

    onChangeAudioVolume,
    onOpenDrawer,
    onClickMute,
    onCloseDrawer,
    onToggleAudioPlay: onToggleAudioPlayProxy,
    onClickPlayer,
    onPauseAudio,

    onNextTrack,
    onPreviousTrack,
    onNextTracklist,
    onPreviousTracklist,

    currentTracklist,
    currentTrack,
    shouldAutoplay,
  });

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
};

export const AudioPlayerContextProvider = memo(AudioPlayerContextProviderBase);
